import { Component, Input, OnInit, OnChanges } from '@angular/core';
import type { SimpleChanges } from '@angular/core';
import { Measure, PerformanceArea, TierData } from 'src/app/shared/models/performance-measures.model';

@Component({
  selector: 'app-odp-resolution-performance-measures',
  templateUrl: './odp-resolution-performance-measures.component.html',
  styleUrls: ['./odp-resolution-performance-measures.component.scss'],
  standalone: false
})
export class OdpResolutionPerformanceMeasuresComponent implements OnInit, OnChanges {
  @Input() performanceAreas: PerformanceArea[] = [];
  @Input() tierMeasureRequirements: TierData[] = [];
  @Input() selectedTierCode: string = '';

  requirementsData: any[] = [];
  dataSource: any[] = [{ data: 'performance-measures' }];

  visibleTiers: { code: string; label: string; headerClass: string; cellClass: string; field: string }[] = [];

  constructor() {}

  ngOnInit(): void {
    console.log('Tier Measure Requirements:', this.tierMeasureRequirements);

    if (this.tierMeasureRequirements && this.tierMeasureRequirements.length > 0) {
      this.prepareTableData();
    }

    this.expandAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tierMeasureRequirements'] && !changes['tierMeasureRequirements'].firstChange) {
      console.log('Tier Measure Requirements updated:', this.tierMeasureRequirements);

      if (this.tierMeasureRequirements && this.tierMeasureRequirements.length > 0) {
        this.prepareTableData();
      }
    }

    if (changes['performanceAreas'] && !changes['performanceAreas'].firstChange) {
      this.expandAll();
    }
  }

  prepareTableData(): void {
    const grouped = new Map<string, any>();

    const sortedTiers = [...this.tierMeasureRequirements]
      .filter(t => t.isTierVisible)
      .sort((a, b) => (a.tierSequence || 0) - (b.tierSequence || 0));

    sortedTiers.forEach(tier => {
      const field = tier.tierCode.toLowerCase();

      (tier.measures || []).forEach(m => {
        if (!grouped.has(m.measureCode)) {
          grouped.set(m.measureCode, {
            measureCode: m.measureCode,
            _seq: m.measureSequence || 0
          });
        }

        const row = grouped.get(m.measureCode);
        row[field] = m.thresholdDesc || '--';
      });
    });

    const allFields = sortedTiers.map(t => t.tierCode.toLowerCase());

    grouped.forEach(row => {
      allFields.forEach(f => {
        if (row[f] === undefined) {
          row[f] = '--';
        }
      });
    });

    this.requirementsData = Array.from(grouped.values())
      .sort((a, b) => a._seq - b._seq)
      .map(({ _seq, ...rest }) => rest);

    this.visibleTiers = sortedTiers.map(tier => ({
      code: tier.tierCode,
      label: tier.tierName,
      field: tier.tierCode.toLowerCase(),
      headerClass: this.getTierHeaderClass(tier.tierCode),
      cellClass: `value-cell ${tier.tierCode.toLowerCase()}-cell`
    }));
  }

  private getTierHeaderClass(tierCode: string): string {
    const code = tierCode.toLowerCase().replace(/[\s_-]/g, '');

    if (code === 'primary') {
      return 'primary-header';
    }

    if (code === 'select') {
      return 'select-header';
    }

    if (code === 'ce' || code === 'clinicallyenhanced') {
      return 'ce-header';
    }

    return 'tier-header';
  }

  expandAll(): void {
    if (!this.performanceAreas || this.performanceAreas.length === 0) {
      return;
    }

    this.performanceAreas.forEach(area => {
      area.expanded = true;

      if (area.measures && area.measures.length > 0) {
        area.measures.forEach(measure => {
          measure.expanded = true;
        });
      }
    });
  }

  toggleArea(areaIndex: number): void {
    if (!this.performanceAreas || !this.performanceAreas[areaIndex]) {
      return;
    }

    this.performanceAreas[areaIndex].expanded = !this.performanceAreas[areaIndex].expanded;
  }

  toggleMeasure(areaIndex: number, measureIndex: number): void {
    if (
      !this.performanceAreas ||
      !this.performanceAreas[areaIndex] ||
      !this.performanceAreas[areaIndex].measures ||
      !this.performanceAreas[areaIndex].measures[measureIndex]
    ) {
      return;
    }

    this.performanceAreas[areaIndex].measures[measureIndex].expanded =
      !this.performanceAreas[areaIndex].measures[measureIndex].expanded;
  }

  trackMeasure(index: number, measure: Measure): string {
    return index.toString();
  }

  getTierBadgeClass(tier: string): string {
    if (!tier) {
      return '';
    }

    const tierLower = tier.toLowerCase();

    if (tierLower === 'primary') {
      return 'badge-primary-level';
    }

    if (tierLower === 'select') {
      return 'badge-select-level';
    }

    if (
      tierLower === 'ce' ||
      tierLower === 'clinicallyenhanced' ||
      tierLower === 'clinically enhanced' ||
      tierLower.includes('clinically enhanced')
    ) {
      return 'badge-clinically-enhanced';
    }

    return 'badge-default';
  }

  getTierLabel(tierCode: string): string {
    if (!tierCode) {
      return '';
    }

    const normalized = this.normalizeTierCode(tierCode);
    const match = this.visibleTiers.find(t => t.code === normalized);

    return match ? match.label : tierCode;
  }

  normalizeTierCode(tier: string): string {
    if (!tier) {
      return '';
    }

    const upper = tier.toUpperCase().replace(/[\s_-]/g, '');
    const match = this.visibleTiers.find(t =>
      t.code.toUpperCase().replace(/[\s_-]/g, '') === upper ||
      t.label.toUpperCase().replace(/[\s_-]/g, '') === upper
    );

    return match ? match.code : tier.toUpperCase();
  }
}